/**
 * 笔顺数据的取用（Hanzi Writer 的 `charDataLoader` 那一层）。
 *
 * ## 数据是什么、从哪来
 *
 * 每个汉字一个 JSON：`{ strokes: [SVG 路径…], medians: [笔画中线的点…] }`，
 * 来自 `hanzi-writer-data`（Make Me a Hanzi 的图形数据，**Arphic Public License**；
 * 库本身 `hanzi-writer` 是 MIT）。构建时由 `vite.config.ts` 的
 * `hanziStrokeData()` 把每个字的 JSON 原样拷进 `dist/strokes/<字>.json`。
 *
 * ## 为什么必须自己判 payload（**这里是本项目最容易踩的一个坑**）
 *
 * 生产环境的 Caddy 对前端是这么配的（见 `deploy/Caddyfile`）：
 *
 *   ```
 *   try_files {path} /index.html
 *   ```
 *
 * 也就是说：**请求一个不存在的 `strokes/某字.json`，服务器会返回 `index.html`，
 * 状态码是 200**（而不是 404）。如果这里只判 `res.ok` 就去 `res.json()`，
 * 会得到一个"Unexpected token '<'"的解析错误，界面上只会说一句"出错了"，
 * 而真正的答案是"这个字不在本地笔顺字库里" —— 两者对家长的意义完全不同。
 * 所以这里**先看 `content-type`**，再看 JSON 形状。
 *
 * ## 为什么不走 CDN
 *
 * `deploy/Caddyfile` 下发的 CSP 是 `connect-src 'self'`，而且明确写了
 * "script-src 保持 'self'：不引入 CDN"。所以笔顺数据只能由**本站**提供 ——
 * 从 CDN 取数据的写法在生产上会被浏览器直接拦掉（本机实测 jsdelivr 也不可达）。
 *
 * ## 失败必须分类（而不是笼统的"加载失败"）
 *
 *   · `not_in_library` —— 字库里没有这个字（返回的是 HTML 兜底页 / 404）。
 *     这是**正常答案**，界面应说"这个字的笔顺暂时没有"，而不是"网络出错了"；
 *   · `network` —— 取不到（离线 / 请求被中断），可以重试；
 *   · `bad_payload` —— 拿到了 JSON 但形状不对（构建产物坏了），必须显式说出来。
 */

/** 一个字的笔顺数据（与 hanzi-writer 的 `CharacterJson` 同形）。 */
export interface HanziStrokeData {
  /** 每一笔的 SVG 路径（顺序 = 笔顺）。 */
  strokes: string[];
  /** 每一笔的中线点集（用来做引导/描红）。 */
  medians: number[][][];
}

/** 取数据的结果：**成功或失败都必须带原因**，不存在"静默成功但数据是空的"。 */
export type StrokeDataResult =
  | { ok: true; data: HanziStrokeData }
  | {
      ok: false;
      reason: 'not_bundled' | 'not_in_library' | 'network' | 'bad_payload';
      detail: string;
    };

/**
 * **这次构建到底有没有把笔顺数据打进产物**（构建时由 `vite.config.ts` 注入）。
 *
 * 这是"整批数据都没有"与"这个字不在字库里"的分界线：前者是构建/部署问题
 * （家长做什么都没用，得让维护者看构建日志），后者只是字库边界（换个字照样能用）。
 * 两者在界面上必须是两句不同的话，否则家长会一个字一个字地试过去。
 *
 * `typeof` 兜底：测试或别的工具（不经 vite 的 `define`）加载本模块时，这个全局
 * 常量并不存在，此时按"没打进"处理 —— 宁可说得保守，也不要抛 ReferenceError
 * 把整个页面带崩。
 */
export const strokesBundled: boolean =
  typeof __HANZI_STROKES__ === 'string' && __HANZI_STROKES__ === 'bundled';

/**
 * 某个字的数据地址。
 *
 * 用 `import.meta.env.BASE_URL` 而不是写死 `/`：产物挂在子路径下时（Vite 的 `base`）
 * 写死的绝对路径会 404，而这种错只在部署后才出现。
 */
export function strokeDataPath(char: string): string {
  return `${import.meta.env.BASE_URL}strokes/${encodeURIComponent(char)}.json`;
}

/** 形状校验：`strokes` 必须是非空的字符串数组，`medians` 必须是数组。 */
function isStrokeData(value: unknown): value is HanziStrokeData {
  if (value === null || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  if (!Array.isArray(record['strokes']) || record['strokes'].length === 0) return false;
  if (!record['strokes'].every((item) => typeof item === 'string')) return false;
  if (!Array.isArray(record['medians'])) return false;
  return true;
}

/**
 * 取一个字的笔顺数据。
 *
 * 永不抛异常：所有失败都变成 `{ ok: false, reason }`，由界面决定怎么说人话。
 */
export async function loadStrokeData(char: string): Promise<StrokeDataResult> {
  /**
   * 构建时就没打进数据 → 一个字都不用去取。理由写在界面上（见
   * `describeStrokeDataFailure`），而不是让每一次点击都去换一个 500 字的 HTML 回来。
   */
  if (!strokesBundled) {
    return {
      ok: false,
      reason: 'not_bundled',
      detail: '构建产物里没有 dist/strokes（构建时没找到 hanzi-writer-data）',
    };
  }

  let response: Response;
  try {
    response = await fetch(strokeDataPath(char), { credentials: 'same-origin' });
  } catch (error) {
    return { ok: false, reason: 'network', detail: String((error as Error)?.message ?? error) };
  }

  /**
   * ⚠️ 先看 content-type：`try_files` 会把"没有这个文件"变成
   *    **200 + index.html**，所以 `response.ok` 本身说明不了任何事。
   */
  const contentType = response.headers.get('content-type') ?? '';
  if (!response.ok || !contentType.includes('json')) {
    return {
      ok: false,
      reason: 'not_in_library',
      detail: `HTTP ${response.status} · ${contentType === '' ? '没有 content-type' : contentType}`,
    };
  }

  let parsed: unknown;
  try {
    parsed = await response.json();
  } catch (error) {
    return { ok: false, reason: 'bad_payload', detail: String((error as Error)?.message ?? error) };
  }

  if (!isStrokeData(parsed)) {
    return { ok: false, reason: 'bad_payload', detail: 'JSON 形状不是 { strokes, medians }' };
  }

  return { ok: true, data: parsed };
}

/**
 * 失败原因 → 一句家长能看懂的话。
 *
 * 刻意把三种分开：
 *   · `not_bundled`  —— **这次构建就没打进数据**（部署/构建问题，家长做什么都没用，
 *     得让维护者看构建日志）。它与"这个字不在字库里"必须说得不一样，否则家长会
 *     一个字一个字地试过去；
 *   · `not_in_library` —— 字库里没有这个字（功能边界，田字格与拼音照常）；
 *   · 另外两种 —— "这次没取到"（可以重试），反复重试才有意义。
 */
export function describeStrokeDataFailure(
  reason: 'not_bundled' | 'not_in_library' | 'network' | 'bad_payload',
): string {
  if (reason === 'not_bundled') {
    return '这次上线的版本里没有放进笔顺数据（构建时没找到数据包），所以笔顺动画暂时都看不了；田字格和拼音不受影响。请把这句话告诉维护这个页面的家长。';
  }
  if (reason === 'not_in_library') {
    return '这个字暂时没有笔顺数据（本地字库里没有它），可以先照着田字格里的字形自己写一遍。';
  }
  if (reason === 'network') {
    return '笔顺数据没取到（网络中断了），可以再点一次这个字试试。';
  }
  return '笔顺数据取回来了，但内容不对（可能是这次构建的产物有问题），这张卡上只有田字格和拼音。';
}
